import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faFilter,
  faPlus,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { format, parseISO } from 'date-fns';
import { api } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import PaymentStatusBadge from '../common/PaymentStatusBadge';
import { getPaymentStatus } from '../../utils/paymentStatus';

const money = (value) => `£${(parseFloat(value) || 0).toFixed(2)}`;

const monthKeyFromDate = (value) => {
  const raw = typeof value === 'string' ? value.slice(0, 10) : '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw.slice(0, 7);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatExpenseDay = (value) => {
  const raw = typeof value === 'string' ? value.slice(0, 10) : '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number);
    return format(new Date(year, month - 1, day), 'd MMM');
  }
  return format(new Date(value), 'd MMM');
};

const monthLabel = (key) => {
  if (!key || key === 'unknown') return 'Unknown';
  const [year, month] = key.split('-').map(Number);
  return format(new Date(year, month - 1, 1), 'MMMM yyyy');
};

const splitLabel = (splitMethod) => {
  switch (splitMethod) {
    case 'FIFTY_FIFTY':
      return '50/50';
    case 'SALARY_PERCENTAGE':
      return 'Salary %';
    case 'FULL_RECLAIM':
      return 'Full reclaim';
    case 'NO_REIMBURSEMENT':
      return 'No reclaim';
    default:
      return 'Split';
  }
};

const emptyFilters = {
  search: '',
  category: '',
  labels: [],
  startDate: '',
  endDate: '',
  minAmount: '',
  maxAmount: '',
  splitType: '',
  paid: '',
  paidBy: '',
  futureExpenses: '',
};

const ExpenseList = () => {
  const { currentUser } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [openMonths, setOpenMonths] = useState(() => new Set());
  const searchDebounce = useRef();
  const currentMonthKey = format(new Date(), 'yyyy-MM');

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [categoriesRes, labelsRes, usersRes] = await Promise.all([
          api.get('/categories'),
          api.get('/labels'),
          api.get('/users'),
        ]);
        setCategories(categoriesRes.data.data || []);
        setLabels(labelsRes.data.data || []);
        setUsers(usersRes.data.data || []);
      } catch (err) {
        console.error('Error loading expense meta:', err);
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
    }, 250);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  useEffect(() => {
    const fetchExpenses = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          ...filters,
          labels: filters.labels.length ? filters.labels.join(',') : undefined,
          sortField,
          sortOrder,
        };

        if (filters.paid === 'true') params.isPaid = 'true';
        else if (filters.paid === 'false' || filters.paid === 'partial') params.isPaid = 'false';
        delete params.paid;

        if (params.paidBy === 'me' && currentUser) params.paidBy = currentUser.id;
        else if (params.paidBy === 'partner' && currentUser) {
          const partner = users.find((user) => user.id !== currentUser.id);
          params.paidBy = partner?.id || undefined;
        }

        Object.keys(params).forEach((key) => {
          if (
            params[key] === '' ||
            params[key] == null ||
            (Array.isArray(params[key]) && params[key].length === 0)
          ) {
            delete params[key];
          }
        });

        const res = await api.get('/expenses', { params });
        let rows = res.data.data || [];
        if (filters.paid === 'partial') {
          rows = rows.filter((expense) => getPaymentStatus(expense) === 'partial');
        } else if (filters.paid === 'false') {
          rows = rows.filter((expense) => getPaymentStatus(expense) === 'pending');
        }
        setExpenses(rows);
      } catch (err) {
        console.error('Error fetching expenses:', err);
        setError(err.response?.data?.message || 'Failed to fetch expenses');
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [filters, sortField, sortOrder, currentUser, users]);

  const monthGroups = useMemo(() => {
    const map = new Map();
    expenses.forEach((expense) => {
      const key = monthKeyFromDate(expense.date);
      if (!map.has(key)) {
        map.set(key, { key, expenses: [], total: 0 });
      }
      const group = map.get(key);
      group.expenses.push(expense);
      group.total += parseFloat(expense.amount) || 0;
    });
    return Array.from(map.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [expenses]);

  useEffect(() => {
    if (!monthGroups.length) {
      setOpenMonths(new Set());
      return;
    }
    const preferred =
      selectedMonth !== 'all' && monthGroups.some((g) => g.key === selectedMonth)
        ? selectedMonth
        : monthGroups.find((g) => g.key === currentMonthKey)?.key || monthGroups[0].key;
    setOpenMonths(new Set([preferred]));
  }, [monthGroups, selectedMonth, currentMonthKey]);

  const visibleGroups = useMemo(() => {
    if (selectedMonth === 'all') return monthGroups;
    return monthGroups.filter((group) => group.key === selectedMonth);
  }, [monthGroups, selectedMonth]);

  const visibleExpenses = useMemo(
    () => visibleGroups.flatMap((group) => group.expenses),
    [visibleGroups]
  );
  const visibleSpend = useMemo(
    () => visibleExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0),
    [visibleExpenses]
  );

  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (key === 'search') return count;
    if (Array.isArray(value)) return count + (value.length ? 1 : 0);
    return count + (value ? 1 : 0);
  }, 0);

  const clearFilters = () => {
    setFilters(emptyFilters);
    setSearchInput('');
    setSelectedMonth('all');
  };

  const toggleMonth = (key) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  if (loading && !expenses.length) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading expenses…</p>
      </div>
    );
  }

  if (error && !expenses.length) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p className="mb-0">{error}</p>
      </Alert>
    );
  }

  return (
    <div className="expenses-page">
      <div className="page-header">
        <div>
          <p className="eyebrow">Ledger</p>
          <h1>Expenses</h1>
          <p className="text-muted mb-0">
            {visibleExpenses.length} item{visibleExpenses.length === 1 ? '' : 's'} ·{' '}
            {money(visibleSpend)}
            {selectedMonth !== 'all' ? ` · ${monthLabel(selectedMonth)}` : ''}
          </p>
        </div>
        <Button as={Link} to="/expenses/new" variant="primary" className="expense-add-btn">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add expense
        </Button>
      </div>

      <section className="panel expense-toolbar mb-3">
        <div className="panel-body">
          <div className="expense-toolbar-row">
            <div className="expense-search">
              <FontAwesomeIcon icon={faSearch} className="expense-search-icon" />
              <Form.Control
                type="search"
                placeholder="Search title or notes"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search expenses"
              />
            </div>
            <Form.Select
              value={`${sortField}:${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split(':');
                setSortField(field);
                setSortOrder(order);
              }}
              aria-label="Sort expenses"
              className="expense-sort-select"
            >
              <option value="date:desc">Newest first</option>
              <option value="date:asc">Oldest first</option>
              <option value="amount:desc">Amount high → low</option>
              <option value="amount:asc">Amount low → high</option>
              <option value="title:asc">Title A → Z</option>
              <option value="title:desc">Title Z → A</option>
            </Form.Select>
            <Button
              variant={showMoreFilters ? 'primary' : 'outline-primary'}
              onClick={() => setShowMoreFilters((v) => !v)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
            </Button>
          </div>

          <div className="expense-status-chips mt-3" role="group" aria-label="Settlement filter">
            {[
              { value: '', label: 'All' },
              { value: 'false', label: 'Pending' },
              { value: 'partial', label: 'Partial' },
              { value: 'true', label: 'Paid' },
            ].map((chip) => (
              <button
                key={chip.value || 'all'}
                type="button"
                className={`expense-chip${filters.paid === chip.value ? ' active' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, paid: chip.value }))}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {showMoreFilters && (
            <div className="expense-more-filters mt-3">
              <div className="expense-filter-grid">
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select name="category" value={filters.category} onChange={handleFilterChange}>
                    <option value="">All categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Split</Form.Label>
                  <Form.Select name="splitType" value={filters.splitType} onChange={handleFilterChange}>
                    <option value="">All splits</option>
                    <option value="percentage">Salary %</option>
                    <option value="equal">50/50</option>
                    <option value="full">Full reclaim</option>
                    <option value="none">No reclaim</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Paid by</Form.Label>
                  <Form.Select name="paidBy" value={filters.paidBy} onChange={handleFilterChange}>
                    <option value="">Anyone</option>
                    <option value="me">Me</option>
                    <option value="partner">Partner</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>When</Form.Label>
                  <Form.Select
                    name="futureExpenses"
                    value={filters.futureExpenses}
                    onChange={handleFilterChange}
                  >
                    <option value="">All dates</option>
                    <option value="past">Past only</option>
                    <option value="future">Future only</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Min £</Form.Label>
                  <Form.Control
                    type="number"
                    name="minAmount"
                    value={filters.minAmount}
                    onChange={handleFilterChange}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Max £</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxAmount"
                    value={filters.maxAmount}
                    onChange={handleFilterChange}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
              </div>
              {labels.length > 0 && (
                <Form.Group className="mt-3">
                  <Form.Label>Labels</Form.Label>
                  <div className="expense-label-chips">
                    {labels.map((label) => {
                      const active = filters.labels.includes(String(label.id));
                      return (
                        <button
                          key={label.id}
                          type="button"
                          className={`expense-chip${active ? ' active' : ''}`}
                          onClick={() => {
                            setFilters((prev) => {
                              const id = String(label.id);
                              const next = active
                                ? prev.labels.filter((item) => item !== id)
                                : [...prev.labels, id];
                              return { ...prev, labels: next };
                            });
                          }}
                        >
                          <span
                            className="expense-chip-dot"
                            style={{ background: label.color || '#6c757d' }}
                          />
                          {label.name}
                        </button>
                      );
                    })}
                  </div>
                </Form.Group>
              )}
              {(activeFilterCount > 0 || searchInput) && (
                <Button variant="link" className="px-0 mt-2" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {monthGroups.length > 0 && (
        <div className="expense-month-strip mb-3" role="tablist" aria-label="Months">
          <button
            type="button"
            role="tab"
            aria-selected={selectedMonth === 'all'}
            className={`expense-month-chip${selectedMonth === 'all' ? ' active' : ''}`}
            onClick={() => setSelectedMonth('all')}
          >
            All
            <small>{expenses.length}</small>
          </button>
          {monthGroups.map((group) => (
            <button
              key={group.key}
              type="button"
              role="tab"
              aria-selected={selectedMonth === group.key}
              className={`expense-month-chip${selectedMonth === group.key ? ' active' : ''}`}
              onClick={() => setSelectedMonth(group.key)}
            >
              {format(parseISO(`${group.key}-01`), 'MMM yyyy')}
              <small>{money(group.total)}</small>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="small text-muted mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Updating…
        </div>
      )}

      {visibleGroups.length === 0 ? (
        <section className="panel">
          <div className="panel-body text-center py-5">
            <p className="text-muted mb-3">No expenses match these filters.</p>
            <Button as={Link} to="/expenses/new" variant="primary">
              Add expense
            </Button>
          </div>
        </section>
      ) : (
        <div className="expense-month-stack">
          {visibleGroups.map((group) => {
            const open = openMonths.has(group.key) || selectedMonth === group.key;
            return (
              <section key={group.key} className="panel expense-month-panel">
                <button
                  type="button"
                  className="expense-month-toggle"
                  onClick={() => toggleMonth(group.key)}
                  aria-expanded={open}
                >
                  <span className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                    <strong>{monthLabel(group.key)}</strong>
                    <Badge bg="secondary" pill>
                      {group.expenses.length}
                    </Badge>
                  </span>
                  <span className="money-figure">{money(group.total)}</span>
                </button>
                {open && (
                  <div className="expense-card-list expense-card-list-always">
                    {group.expenses.map((expense) => {
                      const category = expense.Category || expense.category;
                      return (
                        <Link
                          key={expense.id}
                          to={`/expenses/${expense.id}`}
                          className="expense-card"
                        >
                          <div className="expense-card-top">
                            <div className="min-w-0">
                              <div className="fw-semibold text-truncate">{expense.title}</div>
                              <div className="small text-muted">
                                {formatExpenseDay(expense.date)}
                                {category?.name ? ` · ${category.name}` : ''}
                                {expense.paidBy?.displayName
                                  ? ` · ${expense.paidBy.displayName}`
                                  : ''}
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="money-figure">{money(expense.amount)}</div>
                            </div>
                          </div>
                          <div className="expense-card-meta">
                            <PaymentStatusBadge expense={expense} showRemaining />
                            <Badge bg="light" text="dark" className="border">
                              {splitLabel(expense.splitMethod)}
                            </Badge>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <Link to="/expenses/new" className="expense-fab d-md-none" aria-label="Add expense">
        <FontAwesomeIcon icon={faPlus} />
      </Link>
    </div>
  );
};

export default ExpenseList;
